import { supabase } from "./supabase";
type Result<T> = { data: T | null; error: { message: string } | null };
async function rpc<T>(
  name: string,
  args: Record<string, unknown>,
): Promise<Result<T>> {
  const r = await supabase.rpc(name, args);
  return {
    data: (r.data ?? null) as T | null,
    error: r.error ? { message: r.error.message } : null,
  };
}
export type OperationsDivision = "local_event" | "national";
export type CrewMemberExecution = {
  employee_id: string;
  employee_number: string | null;
  employee_name: string;
  assignment_role: string | null;
  open_time_entry_id: string | null;
  clocked_in_at: string | null;
};
export type FieldCrewUpdate = {
  event_at: string;
  event_type: string;
  details: Record<string, unknown>;
};
export type FieldCrewAssignment = {
  assignment_id: string;
  crew_id: string;
  crew_name: string;
  crew_code: string | null;
  operations_division: OperationsDivision;
  job_id: string;
  job_number: string;
  job_name: string;
  customer_name: string | null;
  order_number: string | null;
  job_status: string;
  priority: string | null;
  phase_id: string;
  phase_type: string;
  phase_code: string | null;
  phase_status: string;
  starts_at: string;
  ends_at: string;
  is_primary: boolean;
  site_name: string | null;
  site_address_line1: string | null;
  site_city: string | null;
  site_state_region: string | null;
  field_session_id: string | null;
  field_session_number: number | null;
  field_session_status: string | null;
  members: CrewMemberExecution[];
  recent_updates: FieldCrewUpdate[];
};
export type FieldCrewWorkspace = {
  contract_version: string;
  organization_id: string;
  capabilities: { supervise: boolean; view_team_time: boolean };
  assignments: FieldCrewAssignment[];
};
export type FieldJobBomComponent = {
  product_id: string | null;
  sku: string | null;
  name: string;
  unit_of_measure?: string | null;
  component_role?: string | null;
  is_optional?: boolean;
  required_quantity_per_unit?: number | null;
  tracking_method?: string | null;
};
export type FieldJobOrderItem = {
  line_key: string;
  line_type: string;
  sku: string | null;
  product_name: string | null;
  description: string;
  quantity: number;
  rental_start_date: string | null;
  rental_end_date: string | null;
  notes: string | null;
  configuration_snapshot: Record<string, unknown> | null;
  bom_components: FieldJobBomComponent[];
};
export type FieldJobAssignment = {
  assignment_id: string;
  crew_id: string;
  crew_name: string;
  crew_code: string | null;
  phase_id: string | null;
  phase_code: string | null;
  phase_type: string | null;
  phase_status: string | null;
  sequence_no: number | null;
  starts_at: string;
  ends_at: string;
  field_session_id: string | null;
  field_session_number: number | null;
  field_session_status: string | null;
  can_complete: boolean;
  completion_blocker?: string | null;
};
export type FieldJobDetail = {
  contract_version: string;
  generated_at: string;
  capabilities: { manage: boolean };
  job: {
    id: string;
    job_name: string;
    job_number: string;
    operations_division: OperationsDivision;
    status: string;
    dispatch_status: string;
    priority: string;
    order_id: string;
    order_number: string | null;
    customer_name: string | null;
    site_name: string | null;
    site_contact_name: string | null;
    site_contact_phone: string | null;
    site_address_line1: string | null;
    site_address_line2: string | null;
    site_city: string | null;
    site_state_region: string | null;
    site_postal_code: string | null;
    site_access_notes: string | null;
    install_requirements: string | null;
    safety_notes: string | null;
    rental_start_date: string | null;
    rental_end_date: string | null;
    delivery_window_start: string | null;
    delivery_window_end: string | null;
    install_window_start: string | null;
    install_window_end: string | null;
    pickup_window_start: string | null;
    pickup_window_end: string | null;
  };
  contacts: {
    id: string;
    role: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    is_primary: boolean;
  }[];
  order_items: FieldJobOrderItem[];
  field_assignments: FieldJobAssignment[];
};
export type FieldTransportVehicleOption = {
  resource_id: string;
  name: string;
  resource_code: string | null;
  vehicle_number: string | null;
  vehicle_type: string;
  available: boolean;
};
export type FieldTransportDriverOption = {
  employee_id: string;
  name: string;
  qualified_vehicle_types: string[];
  available: boolean;
};
export type FieldTransportLeg = {
  route_id: string;
  route_number: string;
  route_type: "delivery" | "return" | string;
  route_status: string;
  phase_id: string;
  phase_type: string;
  phase_status: string;
  scheduled_departure_at: string;
  scheduled_arrival_at: string;
  actual_departure_at: string | null;
  actual_arrival_at: string | null;
  origin_name: string | null;
  destination_name: string | null;
  vehicle: {
    resource_id: string;
    name: string;
    resource_code: string | null;
    vehicle_number: string | null;
    vehicle_type: string | null;
  } | null;
  driver: { employee_id: string; name: string } | null;
  vehicle_options: FieldTransportVehicleOption[];
  driver_options: FieldTransportDriverOption[];
  ready: boolean;
};
export type FieldJobTransportWorkspace = {
  contract_version: string;
  job_id: string;
  capabilities: { execute: boolean; manage: boolean };
  legs: FieldTransportLeg[];
};
export type FieldCalendarSubscriptionStatus = {
  contract_version: string;
  active: boolean;
  subscription_id: string | null;
  token_prefix: string | null;
  created_at: string | null;
  last_accessed_at: string | null;
};
export type FieldCalendarSubscription = {
  contract_version: string;
  subscription_id: string;
  token: string;
  token_prefix: string;
  created_at: string;
};
export const fieldCrewApi = {
  workspace: (organizationId: string, startDate: string, endDate: string) =>
    rpc<FieldCrewWorkspace>("get_field_crew_execution_workspace", {
      p_organization_id: organizationId,
      p_start_date: startDate,
      p_end_date: endDate,
    }),
  jobDetail: (jobId: string) =>
    rpc<FieldJobDetail>("get_field_job_detail_v2", { p_job_id: jobId }),
  completeAssignedPhase: (assignmentId: string) =>
    rpc<string>("complete_field_assigned_phase", {
      p_crew_assignment_id: assignmentId,
    }),
  transport: (jobId: string) =>
    rpc<FieldJobTransportWorkspace>("get_field_job_transport_workspace", {
      p_job_id: jobId,
    }),
  assignTransport: (
    routeId: string,
    vehicleResourceId: string,
    driverEmployeeId: string,
  ) =>
    rpc<string>("field_assign_job_transport", {
      p_route_id: routeId,
      p_vehicle_resource_id: vehicleResourceId,
      p_driver_employee_id: driverEmployeeId,
    }),
  departTransport: (routeId: string) =>
    rpc<string>("field_depart_job_transport", { p_route_id: routeId }),
  arriveTransport: (routeId: string) =>
    rpc<string>("field_arrive_job_transport", { p_route_id: routeId }),
  calendarStatus: (organizationId: string) =>
    rpc<FieldCalendarSubscriptionStatus>(
      "get_field_calendar_subscription_status",
      { p_organization_id: organizationId },
    ),
  createCalendarSubscription: (organizationId: string) =>
    rpc<FieldCalendarSubscription>("create_field_calendar_subscription", {
      p_organization_id: organizationId,
    }),
  revokeCalendarSubscription: (organizationId: string) =>
    rpc<boolean>("revoke_field_calendar_subscription", {
      p_organization_id: organizationId,
    }),
  calendarFeedUrl: (token: string) =>
    `${String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/$/, "")}/functions/v1/field-calendar?token=${encodeURIComponent(token)}`,
  clockIn: (assignmentId: string, employeeId: string, notes: string | null) =>
    rpc<string>("field_supervisor_clock_in_member", {
      p_crew_assignment_id: assignmentId,
      p_employee_id: employeeId,
      p_client_event_id: crypto.randomUUID(),
      p_occurred_at: new Date().toISOString(),
      p_notes: notes,
    }),
  clockOut: (assignmentId: string, employeeId: string, notes: string | null) =>
    rpc<string>("field_supervisor_clock_out_member", {
      p_crew_assignment_id: assignmentId,
      p_employee_id: employeeId,
      p_client_event_id: crypto.randomUUID(),
      p_occurred_at: new Date().toISOString(),
      p_notes: notes,
    }),
  postUpdate: (
    assignmentId: string,
    updateType: string,
    message: string,
    progress: number | null,
  ) =>
    rpc<number>("field_supervisor_post_job_update", {
      p_crew_assignment_id: assignmentId,
      p_update_type: updateType,
      p_message: message,
      p_progress_percent: progress,
      p_client_event_id: crypto.randomUUID(),
      p_occurred_at: new Date().toISOString(),
    }),
};
